import React, { useState, useEffect } from 'react';

interface CheckoutTimerProps {
    selected: boolean;
    config: {
        timerType?: 'minutes' | 'date';
        backgroundColor?: string;
        textColor?: string;
        timeValue?: number; // em minutos para timer tipo 'minutes'
        endDate?: string; // para timer tipo 'date'
        activeText?: string;
        expiredText?: string;
        fixedTop?: boolean;
    };
    onSelect: () => void;
    onDelete?: () => void;
    onDuplicate?: () => void;
    dragHandleProps?: any;
    viewMode?: boolean; // Modo visualização (não editável)
}

const CheckoutTimer: React.FC<CheckoutTimerProps> = ({
    selected,
    config,
    onSelect,
    onDelete,
    onDuplicate,
    dragHandleProps,
    viewMode = false
}) => {
    const [timeLeft, setTimeLeft] = useState<number>(0);
    const [isExpired, setIsExpired] = useState(false);

    // Configurações padrão
    const timerType = config.timerType || 'minutes';
    const backgroundColor = config.backgroundColor || '#ef4444';
    const textColor = config.textColor || '#ffffff';
    const timeValue = config.timeValue || 15; // 15 minutos por padrão
    const activeText = config.activeText || 'Oferta por tempo limitado';
    const expiredText = config.expiredText || 'O tempo acabou!';
    const isFixedTop = viewMode && (config.fixedTop === undefined || config.fixedTop === true);

    // Calcular tempo inicial
    useEffect(() => {
        if (timerType === 'minutes') {
            const initial = timeValue * 60; // converter minutos para segundos
            setTimeLeft(initial);
            setIsExpired(initial <= 0);
        } else if (timerType === 'date' && config.endDate) {
            const endTime = new Date(config.endDate).getTime();
            const now = new Date().getTime();
            const difference = Math.max(0, Math.floor((endTime - now) / 1000));
            setTimeLeft(difference);
            setIsExpired(difference <= 0);
        }
    }, [timerType, timeValue, config.endDate]);

    // Contagem regressiva
    useEffect(() => {
        if (timeLeft <= 0) {
            // Se já chegou em zero (ou ainda não foi inicializado direito), apenas marcar expirado
            setIsExpired(true);
            return;
        }

        // Enquanto houver tempo restante, garantir que esteja no estado "ativo"
        setIsExpired(false);

        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    setIsExpired(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [timeLeft]);

    // Formatar tempo (MM:SS ou HH:MM:SS)
    const formatTime = (seconds: number): string => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        if (hours > 0) {
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Se estiver em viewMode, renderizar apenas o timer sem interações
    if (viewMode) {
        // Quando fixedTop estiver ativo no checkout, grudamos o timer no topo
        // em todas as larguras de tela. Se fixedTop for false, ele rola normalmente.
        const fixedClasses = isFixedTop
            ? ' fixed top-0 left-0 right-0 z-40'
            : '';

        return (
            <div
                className={`w-full py-3 px-6 text-center overflow-hidden${fixedClasses}`}
                style={{
                    backgroundColor,
                    color: textColor
                }}
            >
                <div className="flex items-center justify-center gap-4">
                    {/* Ícone de relógio */}
                    <i className="ri-time-line text-xl"></i>

                    {/* Tempo */}
                    <div className="text-2xl font-bold">
                        {formatTime(timeLeft)}
                    </div>

                    {/* Texto */}
                    <div className="text-base font-medium">
                        {isExpired ? expiredText : activeText}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`relative group w-full cursor-pointer ${selected ? 'ring-2 ring-blue-500' : 'hover:ring-2 hover:ring-blue-300'}`}
            onClick={onSelect}
        >
            {/* Timer Display */}
            <div
                className="w-full py-4 px-6 text-center relative overflow-hidden"
                style={{
                    backgroundColor,
                    color: textColor
                }}
            >
                <div className="flex items-center justify-center gap-4">
                    {/* Ícone de relógio */}
                    <i className="ri-time-line text-xl"></i>

                    {/* Tempo */}
                    <div className="text-2xl font-bold">
                        {formatTime(timeLeft)}
                    </div>

                    {/* Texto */}
                    <div className="text-base font-medium">
                        {isExpired ? expiredText : activeText}
                    </div>
                </div>
            </div>

            {/* Controles do componente */}
            {selected && (
                <div className="absolute top-2 right-2 flex gap-1 z-10">
                    <span
                        {...dragHandleProps}
                        className="p-1.5 rounded bg-white shadow-lg border text-gray-600 hover:text-gray-800 cursor-grab active:cursor-grabbing"
                        title="Arrastar"
                    >
                        <i className="ri-drag-move-2-line text-sm"></i>
                    </span>
                    {onDuplicate && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onDuplicate();
                            }}
                            className="p-1.5 rounded bg-white shadow-lg border text-gray-600 hover:text-gray-800"
                            title="Duplicar"
                        >
                            <i className="ri-file-copy-line text-sm"></i>
                        </button>
                    )}
                    {onDelete && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete();
                            }}
                            className="p-1.5 rounded bg-white shadow-lg border text-red-600 hover:text-red-800"
                            title="Excluir"
                        >
                            <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default CheckoutTimer;
